'use client'

import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'
import { motion } from 'framer-motion'
import { MessageCircle } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'

import { ClickableHashtagContent } from '@/components/common/clickable-hashtag-content'
import { PostOptionsDropdown } from '@/components/common/post-options-dropdown'
import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { Card, CardContent, CardFooter } from '@/components/ui/card'

import { useAuth } from '@/contexts/auth-context'
import { useMemePostModal } from '@/contexts/meme-post-modal-context'
import memeService from '@/services/meme.service'
import { isAdmin, isInstructor } from '@/utils/admin-utils'
import { generateMemeSEO } from '@/utils/seo-utils'

import ImageMemeSlideShow from './image-slideshow'
import MemePostReactions, { defaultReactions, renderReactionDisplay } from './meme-post-reactions'
import { getDisplayName } from './post-detail-modal'
import { UserAvatarPopover } from '../common/modal/avatar-hover'
import { SocialShareButtons } from '../common/social-share-buttons'
import { ReactionListDialog } from '../reactions/reaction-list-dialog'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'

import { EmojiData, MemePost } from '@/types/meme.interface'

interface PostMemeCardProps {
  post: MemePost
  onToggleReaction?: (postId: string, reactionType?: string, emojiData?: EmojiData) => Promise<void>
  isAuthenticated?: boolean
  onDelete?: (postId: string) => void
  onEdit?: (post: MemePost) => void
  isPinned?: boolean
  onPin?: (postId: string) => void
  onUnpin?: (postId: string) => void
}

export default function PostMemeCard({
  post,
  onToggleReaction,
  isAuthenticated = false,
  onDelete,
  onEdit,
  isPinned,
  onPin,
  onUnpin,
}: PostMemeCardProps) {
  const [isReacting, setIsReacting] = React.useState(false)
  const [showReactionList, setShowReactionList] = React.useState(false)
  const { openModal } = useMemePostModal('MemePostModal')
  const [openEditor, setOpenEditor] = React.useState(false)

  const queryClient = useQueryClient()

  const { user } = useAuth()

  const seoData = generateMemeSEO(post)
  const shareUrl = React.useMemo(() => `${window.location.origin}/forum/shared/${post.id}`, [post.id])

  const handleReactionClick = async (postId: string, reactionType: string, emojiData: EmojiData) => {
    if (!onToggleReaction || isReacting || !isAuthenticated) return

    setIsReacting(true)
    try {
      await onToggleReaction(postId, reactionType, emojiData)
    } finally {
      setIsReacting(false)
    }
  }

  const handleOpenModal = async () => {
    try {
      const postDetail = await memeService.getMemeDetail(post.id)
      openModal(postDetail)
    } catch (error) {
      toast({
        title: 'Failed to open modal',
        description: 'Please try again later',
        variant: 'destructive',
      })
    }
  }

  const handleRemoveCurrentUserReaction = async (postId: string) => {
    try {
      await memeService.removePostReaction(postId)
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      queryClient.invalidateQueries({ queryKey: ['pinned-posts'] })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove current user reaction:', error)
    }
  }

  const postImages = post.images ?? []

  return (
    <Card
      className={cn('bg-white shadow-sm border border-gray-200 flex flex-col justify-between', {
        'aspect-square': postImages.length > 0,
      })}
    >
      <CardContent className="p-0">
        <div
          className={cn('p-4 border-b border-gray-100', {
            'border-b-0': postImages.length === 0,
          })}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-3">
              {post.isAnonymous ? (
                <Avatar className="h-10 w-10">
                  <ResolvedAvatarImage
                    avatar={post?.user?.avatar}
                    alt={`${post?.user?.firstName} ${post?.user?.lastName}`}
                  />
                  <AvatarFallback>
                    {post?.user?.firstName[0]}
                    {post?.user?.lastName[0]}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <UserAvatarPopover user={post.user} />
              )}
              <div className="flex-1">
                {!post.isAnonymous ? (
                  <Link href={`/user/${post?.user?.profileId || ''}`} passHref>
                    <div className="font-semibold text-gray-900 hover:underline">
                      {post?.user?.firstName} {post?.user?.lastName}
                    </div>
                  </Link>
                ) : (
                  <div className="font-semibold text-gray-900">
                    {getDisplayName({ firstName: post.user.firstName, lastName: post.user.lastName })}
                  </div>
                )}
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  {formatDistanceToNow(post.createdAt, {
                    addSuffix: true,
                    locale: vi,
                  })}
                  {user?.id === post?.user?.id && (
                    <Badge variant="outline" className="text-blue-600 border-blue-600">
                      Me
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div>
              {(user?.id === post?.user?.id || isAdmin(user) || isInstructor(user)) && (
                <PostOptionsDropdown
                  showPin={Boolean(onPin && !isPinned)}
                  showUnpin={Boolean(onUnpin && isPinned)}
                  showEdit={user?.id === post?.user?.id && Boolean(onEdit)}
                  showDelete={Boolean((user?.id === post?.user?.id || isAdmin(user)) && onDelete)}
                  onPin={onPin ? () => onPin(post.id) : undefined}
                  onUnpin={onUnpin ? () => onUnpin(post.id) : undefined}
                  onEdit={onEdit ? () => onEdit(post) : undefined}
                  onDelete={onDelete ? () => onDelete(post.id) : undefined}
                  open={openEditor}
                  onOpenChange={setOpenEditor}
                />
              )}
            </div>
          </div>

          <div className="ql-container ql-snow">
            <ClickableHashtagContent content={post.content} as="article" className="" />
          </div>
        </div>

        {/* Post Content */}
        <div className="p-4 pt-0">
          {/* Meme Image */}
          {postImages.length > 0 ? (
            <ImageMemeSlideShow
              slides={postImages
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(img => ({ alt: img.id, src: img.imageUrl }))}
            />
          ) : null}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2 w-full p-0">
        <EngagementSummaryBar post={post} />
        {/* Post Actions */}
        <div className="px-4 pb-4 w-full">
          <div className="flex pt-2 border-t border-gray-200 gap-2">
            <MemePostReactions
              postId={post.id}
              reactions={post.reactions || []}
              onReact={handleReactionClick}
              currentUser={user}
              isAuthenticated={isAuthenticated}
              isReacting={isReacting}
              className="flex-1 w-full"
              onRemoveCurrentUserReaction={handleRemoveCurrentUserReaction}
            />
            <Button
              variant="ghost"
              className="flex items-center gap-2 py-2 text-gray-600 hover:bg-gray-100 flex-1"
              onClick={handleOpenModal}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm hidden sm:block">
                Bình luận {post.commentCount > 0 && `(${post.commentCount})`}
              </span>
            </Button>
            {/* Enhanced Social Share Buttons */}
            <SocialShareButtons
              url={shareUrl}
              title={seoData.title}
              description={seoData.description}
              id={post.id}
              type="post"
              hashtags={seoData.tags}
              variant="ghost"
              size="sm"
              className="flex-1 w-full"
              textContent={<span className="hidden sm:inline">Chia sẻ</span>}
              modal={false}
            />
          </div>
        </div>
      </CardFooter>
      <ReactionListDialog
        isOpen={showReactionList}
        onClose={() => setShowReactionList(false)}
        reactions={post.reactions || []}
      />
    </Card>
  )
}

export function EngagementSummaryBar({ post, className }: { post: MemePost; className?: string }) {
  const { user: currentUser } = useAuth()
  const { openModal } = useMemePostModal('MemePostModal')
  const [showReactionList, setShowReactionList] = React.useState(false)

  const groupedReactions = React.useMemo(() => {
    const reactions = post.reactions || []
    const groups = new Map<
      string,
      {
        identifier: string
        reactionType: string
        emojiData?: EmojiData
        count: number
        users: Array<{ id: number; name: string }>
        userReacted: boolean
        isDefault: boolean
      }
    >()

    reactions.forEach(reaction => {
      // Skip reactions with missing user data (can happen with deleted users or incomplete API responses)
      if (!reaction.user) return

      // Create unique identifier for grouping
      let identifier: string
      let isDefault = false

      if (defaultReactions[reaction.reactionType]) {
        // It's a default reaction
        identifier = reaction.reactionType
        isDefault = true
      } else if (reaction.emojiData?.unicode) {
        // It's a custom emoji
        identifier = reaction.emojiData.unicode
      } else if (reaction.emojiData?.imageUrl) {
        // It's a custom meme
        identifier = reaction.emojiData.imageUrl
      } else {
        // Fallback
        identifier = reaction.reactionType
      }

      const existing = groups.get(identifier)

      if (existing) {
        existing.count++
        existing.users.push({
          id: reaction.user.id,
          name: `${reaction.user.firstName} ${reaction.user.lastName}`,
        })
        if (reaction.user.id === currentUser?.id) {
          existing.userReacted = true
        }
      } else {
        groups.set(identifier, {
          identifier,
          reactionType: reaction.reactionType,
          emojiData: reaction.emojiData,
          count: 1,
          users: [
            {
              id: reaction.user.id,
              name: `${reaction.user.firstName} ${reaction.user.lastName}`,
            },
          ],
          userReacted: reaction.user.id === currentUser?.id,
          isDefault,
        })
      }
    })

    return Array.from(groups.values()).sort((a, b) => {
      if (a.userReacted && !b.userReacted) return -1
      if (!a.userReacted && b.userReacted) return 1
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1
      return b.count - a.count
    })
  }, [currentUser?.id, post.reactions])

  return (
    <div className={cn('pb-2 px-8 w-full', className)}>
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center gap-1">
          {groupedReactions.length > 0 && (
            <div className="flex items-center gap-1">
              <div className="flex -space-x-0.5">
                {groupedReactions.slice(0, 3).map((group, index) => (
                  <motion.div
                    key={`${group.identifier}-${index}`}
                    className="rounded-full flex items-center justify-center shadow-sm border border-white bg-gray-50 cursor-pointer"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 10,
                      delay: index * 0.05,
                    }}
                    whileHover={{
                      scale: 1.3,
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.95 }}
                    title={`${group.count} reaction${group.count > 1 ? 's' : ''} - ${group.users.map(u => u.name).join(', ')}`}
                  >
                    {renderReactionDisplay(group, 'medium')}
                  </motion.div>
                ))}
              </div>

              {post.reactions && post.reactions.length > 0 && (
                <motion.div
                  className="text-xs font-medium bg-gray-100 hover:bg-gray-200 px-1 py-0.5 rounded-full text-gray-600 cursor-pointer transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={`${post.reactions.length} total reactions`}
                  onClick={() => setShowReactionList(true)}
                >
                  {post.reactions.length}
                </motion.div>
              )}
            </div>
          )}

          <ReactionListDialog
            isOpen={showReactionList}
            onClose={() => setShowReactionList(false)}
            reactions={post.reactions || []}
          />
        </div>
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => openModal(post)}>
          <span className="hover:underline">{post.commentCount || 0} bình luận</span>
        </div>
      </div>
    </div>
  )
}
